import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Snowflake } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "tech" ? "/tech" : "/office", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F5F6FA]">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[42%] bg-[#181854] text-white p-12 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#4F9CE0] opacity-10 blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full bg-[#4F9CE0] opacity-10 blur-3xl" />

        <div className="relative z-10">
          <div className="bg-white rounded-2xl px-5 py-4 inline-flex items-center justify-center shadow-md">
            <img
              src="/brand/gt-logo.webp"
              alt="GT Refrigeration & Aircon Services"
              className="h-12 w-auto object-contain"
              style={{ maxWidth: "260px" }}
            />
          </div>
        </div>

        <div className="relative z-10 space-y-4">
          <p className="text-white/60 uppercase tracking-widest text-xs flex items-center gap-2">
            <Snowflake size={13} /> GT Field OS
          </p>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            Commercial Cooling.
            <br />
            <span className="text-[#4F9CE0]">Performance Proven.</span>
          </h1>
          <p className="text-white/70 max-w-md leading-relaxed">
            One system from booking to invoice. Big buttons, plain language, no
            fluff — built for the way GT actually runs a job.
          </p>
        </div>

        <div className="relative z-10 text-xs text-white/40">
          © {new Date().getFullYear()} GT Refrigeration &amp; Aircon Services
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center justify-center">
            <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-[#E4E6EF]">
              <img
                src="/brand/gt-logo.webp"
                alt="GT Refrigeration & Aircon Services"
                className="h-10 w-auto object-contain"
                style={{ maxWidth: "220px" }}
              />
            </div>
          </div>

          <div className="hidden lg:flex mb-8 items-center justify-center">
            <img
              src="/brand/gt-logo.webp"
              alt="GT Refrigeration & Aircon Services"
              className="h-12 w-auto object-contain"
              style={{ maxWidth: "200px" }}
              data-testid="login-logo"
            />
          </div>

          <h2 className="text-3xl font-bold text-[#181854] tracking-tight">
            Sign in
          </h2>
          <p className="text-[#6A6E85] mt-2 mb-8">
            Use your GT Field OS account to continue.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#6A6E85]">
                Email
              </label>
              <input
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] focus:ring-2 focus:ring-[#4F9CE0]/20 outline-none transition-colors duration-200 bg-white"
                placeholder="you@gtrefrig.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#6A6E85]">
                Password
              </label>
              <input
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 rounded-lg border border-[#E4E6EF] focus:border-[#4F9CE0] focus:ring-2 focus:ring-[#4F9CE0]/20 outline-none transition-colors duration-200 bg-white"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full bg-[#181854] hover:bg-[#101038] text-white py-3 rounded-lg font-semibold transition-colors duration-200 disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
